// components/navbar/NavbarMobile.client.tsx — бургер + мобильное меню + (опционально) Radix hover меню только для desktop, но лучше оставить в server (ниже объясню)

'use client';

import clsx from 'clsx';
import type { LucideIcon } from 'lucide-react';
import { ChevronDown, ChevronUp, Menu, X } from 'lucide-react';
import type { Session } from 'next-auth';
import Link from 'next/link';
import { useCallback, useEffect, useState } from 'react';

import { ProfileArea } from './ProfileArea.client';
import { ReporterLabel } from './ReporterLabel';

type Tab = {
  id: number;
  label: string;
  href: string;
  Icon: LucideIcon;
  kind?: 'reporter' | 'default';
};

type FinanceLink = { name: string; href: string };

function isActive(pathname: string, href: string) {
  if (href === '/') return pathname === '/';
  return pathname.startsWith(href);
}

export function NavbarMobile({
  tabs,
  financeLinks,
  pathname,
  isAdmin,
  session,
}: {
  tabs: Tab[];
  financeLinks: FinanceLink[];
  pathname: string;
  isAdmin: boolean;
  session: Session | null;
}) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [financeOpen, setFinanceOpen] = useState(false);

  useEffect(() => {
    // при смене маршрута обычно хочешь закрыть меню — но pathname приходит сверху,
    // поэтому просто закрывай через useEffect если будешь передавать pathname обновляясь.
    setMenuOpen(false);
    setFinanceOpen(false);
  }, [pathname]);

  const toggleMobileMenu = useCallback(() => setMenuOpen((p) => !p), []);
  const toggleFinanceMobile = useCallback(() => setFinanceOpen((p) => !p), []);
  const closeMobileMenu = useCallback(() => setMenuOpen(false), []);

  return (
    <div className="w-full">
      <div className="flex justify-between items-center w-full">
        <div className="flex items-center gap-2">
          <ProfileArea session={session} compact />
        </div>

        <div className="flex items-center gap-2">
          {isAdmin && (
            <Link href="/admin" aria-label="Панель администратора" className="text-white text-sm">
              Админка
            </Link>
          )}

          <button className="text-white" onClick={toggleMobileMenu} aria-label="Открыть меню">
            {menuOpen ? <X size={28} /> : <Menu size={28} />}
          </button>
        </div>
      </div>

      {/* Мобильное меню — без framer (CSS transition), легче и стабильнее */}
      <nav
        aria-label="Мобильное меню"
        className={clsx(
          'mt-3 overflow-hidden rounded-xl bg-gradient-to-b from-indigo-600 to-blue-500 dark:from-slate-900 dark:to-slate-950 shadow-lg transition-[max-height,opacity] duration-300',
          menuOpen ? 'max-h-[70vh] opacity-100' : 'max-h-0 opacity-0',
        )}
      >
        <ul className="flex flex-col divide-y divide-white/20">
          {tabs.map((tab) =>
            tab.href === '/finance' ? (
              <li key={tab.id}>
                <button
                  onClick={toggleFinanceMobile}
                  aria-expanded={financeOpen}
                  aria-controls="finance-submenu"
                  className="w-full flex justify-between items-center px-4 py-3 hover:bg-white/20 text-white"
                >
                  <span className="flex items-center gap-2">
                    <tab.Icon size={18} />
                    <span>{tab.label}</span>
                  </span>

                  {financeLinks.length > 0 &&
                    (financeOpen ? <ChevronUp size={18} /> : <ChevronDown size={18} />)}
                </button>

                <div
                  id="finance-submenu"
                  className={clsx(
                    'overflow-hidden transition-[max-height,opacity] duration-300',
                    financeOpen ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0',
                  )}
                >
                  <ul className="ml-6">
                    {financeLinks.map((link) => (
                      <li key={link.href}>
                        <Link
                          href={link.href}
                          onClick={() => {
                            closeMobileMenu();
                            setFinanceOpen(false);
                          }}
                          aria-current={pathname === link.href ? 'page' : undefined}
                          className={clsx(
                            'block px-8 py-2 text-sm hover:bg-white/20 text-white',
                            pathname === link.href && 'bg-white/30 font-semibold',
                          )}
                        >
                          {link.name}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              </li>
            ) : (
              <li key={tab.id}>
                <Link
                  prefetch={false}
                  href={tab.href}
                  onClick={closeMobileMenu}
                  aria-current={isActive(pathname, tab.href) ? 'page' : undefined}
                  className={clsx(
                    'flex items-center gap-2 px-4 py-3 hover:bg-white/20 text-white',
                    isActive(pathname, tab.href) && 'bg-white/30 font-semibold',
                  )}
                >
                  <tab.Icon size={18} />
                  <span>{tab.kind === 'reporter' ? <ReporterLabel /> : tab.label}</span>
                </Link>
              </li>
            ),
          )}
        </ul>
      </nav>
    </div>
  );
}
